import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import socket from "../lib/socket";

function useTransparentBody() {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlBg: html.style.background,
      htmlBgImg: html.style.backgroundImage,
      bodyBg: body.style.background,
      bodyBgImg: body.style.backgroundImage,
      bodyMargin: body.style.margin,
      bodyOverflow: body.style.overflow,
    };

    html.style.background = "transparent";
    html.style.backgroundImage = "none";
    body.style.background = "transparent";
    body.style.backgroundImage = "none";
    body.style.margin = "0";
    body.style.overflow = "hidden";

    return () => {
      html.style.background = prev.htmlBg;
      html.style.backgroundImage = prev.htmlBgImg;
      body.style.background = prev.bodyBg;
      body.style.backgroundImage = prev.bodyBgImg;
      body.style.margin = prev.bodyMargin;
      body.style.overflow = prev.bodyOverflow;
    };
  }, []);
}

function normalizeChannelSlug(channelId) {
  const raw = String(channelId || "").trim();
  if (!raw) return "";
  if (raw.includes(":")) {
    const parts = raw.split(":").map((x) => x.trim()).filter(Boolean);
    return parts[parts.length - 1] || "";
  }
  return raw;
}

function roomVariantsForSlug(slug) {
  const key = String(slug || "").trim();
  if (!key) return [];
  return [`live:${key}`, `vod:${key}`];
}

function roomMatches(payloadRoom, rooms) {
  const value = String(payloadRoom || "").trim();
  if (!value) return true;
  return rooms.includes(value);
}

function SpecialArrivalCard({ alert }) {
  if (!alert) return null;

  const toneStyles = {
    lost: {
      label: "Long-time GA check-in",
      accent: "#ffd27a",
      glow: "rgba(255,110,0,0.45)",
      border: "rgba(255,180,70,0.62)",
      bg: "linear-gradient(135deg, rgba(90,14,0,0.88), rgba(0,0,0,0.80), rgba(255,190,0,0.17))",
      wash: "radial-gradient(circle at center, rgba(255,80,0,0.20), rgba(0,0,0,0.02) 54%, transparent 74%)",
      scan: "rgba(255,190,80,0.20)",
    },
    dq: {
      label: "Dead Homiez check-in",
      accent: "#b9d7ff",
      glow: "rgba(120,90,255,0.45)",
      border: "rgba(170,150,255,0.62)",
      bg: "linear-gradient(135deg, rgba(40,10,80,0.88), rgba(0,0,0,0.82), rgba(90,170,255,0.17))",
      wash: "radial-gradient(circle at center, rgba(120,90,255,0.20), rgba(0,0,0,0.02) 54%, transparent 74%)",
      scan: "rgba(160,190,255,0.20)",
    },
    slim: {
      label: "Big 4s check-in",
      accent: "#8dffb6",
      glow: "rgba(0,255,130,0.42)",
      border: "rgba(110,255,170,0.62)",
      bg: "linear-gradient(135deg, rgba(0,66,30,0.88), rgba(0,0,0,0.82), rgba(180,70,255,0.18))",
      wash: "radial-gradient(circle at center, rgba(0,255,130,0.18), rgba(0,0,0,0.02) 54%, transparent 74%)",
      scan: "rgba(120,255,180,0.20)",
    },
    debacle: {
      label: "COD GOD check-in",
      accent: "#ffb26b",
      glow: "rgba(255,55,20,0.48)",
      border: "rgba(255,120,70,0.66)",
      bg: "linear-gradient(135deg, rgba(96,10,0,0.90), rgba(0,0,0,0.84), rgba(255,210,80,0.16))",
      wash: "radial-gradient(circle at center, rgba(255,45,15,0.20), rgba(0,0,0,0.02) 54%, transparent 74%)",
      scan: "rgba(255,160,80,0.22)",
    },
  };

  const tone = String(alert.tone || "dq").toLowerCase();
  const fx = toneStyles[tone] || toneStyles.dq;
  const accent = fx.accent;
  const glow = fx.glow;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 20,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: fx.wash,
      }}
    >
      <style>{`
        @keyframes gaSpecialArrivalPop {
          0% { opacity: 0; transform: translateY(-2vh) scale(.82) rotateX(12deg); filter: blur(8px); }
          12% { opacity: 1; transform: translateY(-4vh) scale(1.04) rotateX(0deg); filter: blur(0); }
          20% { transform: translateY(-4vh) scale(1); }
          82% { opacity: 1; transform: translateY(-4vh) scale(1); filter: blur(0); }
          100% { opacity: 0; transform: translateY(-5vh) scale(.94); filter: blur(5px); }
        }
        @keyframes gaSpecialArrivalSpark {
          from { transform: translateX(-22%) rotate(0deg); opacity: .50; }
          to { transform: translateX(22%) rotate(360deg); opacity: .18; }
        }
        @keyframes gaSpecialScanline {
          0% { transform: translateY(-130%); opacity: 0; }
          16% { opacity: .72; }
          50% { opacity: .36; }
          100% { transform: translateY(130%); opacity: 0; }
        }
      `}</style>

      <div
        style={{
          width: "min(820px, 90vw)",
          minHeight: 230,
          borderRadius: 36,
          border: `1px solid ${fx.border}`,
          background: fx.bg,
          boxShadow: `0 0 50px ${glow}, inset 0 0 70px rgba(255,255,255,0.06)`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 14,
          textAlign: "center",
          padding: "28px 32px",
          transform: "translateY(-4vh)",
          animation: "gaSpecialArrivalPop 8.5s ease both",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: -100,
            background:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.40) 0 3px, transparent 4px), radial-gradient(circle at 70% 30%, rgba(255,80,80,0.38) 0 4px, transparent 5px), radial-gradient(circle at 45% 75%, rgba(255,210,70,0.34) 0 4px, transparent 5px)",
            backgroundSize: "110px 110px, 150px 150px, 180px 180px",
            animation: "gaSpecialArrivalSpark 8.5s linear both",
            opacity: 0.58,
          }}
        />
        <div
          style={{
            position: "absolute",
            left: "-20%",
            right: "-20%",
            top: "50%",
            height: 56,
            background: `linear-gradient(90deg, transparent, ${fx.scan}, transparent)`,
            filter: "blur(2px)",
            animation: "gaSpecialScanline 2.8s ease-out both",
          }}
        />
        <div
          style={{
            position: "relative",
            fontSize: "clamp(18px, 2vw, 26px)",
            letterSpacing: 5,
            textTransform: "uppercase",
            fontWeight: 950,
            color: accent,
            textShadow: "0 4px 22px rgba(0,0,0,0.85)",
          }}
        >
          {fx.label}
        </div>
        <div
          style={{
            position: "relative",
            fontSize: "clamp(34px, 5vw, 72px)",
            lineHeight: 1.02,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            fontWeight: 1000,
            color: "#fff",
            textShadow:
              "0 4px 0 rgba(0,0,0,0.88), 0 0 28px rgba(255,255,255,0.26), 0 0 48px rgba(255,0,70,0.24)",
          }}
        >
          {alert.title}
        </div>
        {alert.subtitle ? (
          <div
            style={{
              position: "relative",
              fontSize: "clamp(14px, 1.7vw, 22px)",
              letterSpacing: 2.5,
              textTransform: "uppercase",
              fontWeight: 900,
              color: "rgba(255,255,255,0.88)",
            }}
          >
            {alert.subtitle}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function OverlaySpecial() {
  const { channelId } = useParams();
  const slug = normalizeChannelSlug(channelId);
  const rooms = useMemo(() => roomVariantsForSlug(slug), [slug]);
  const room = rooms[0] || "";
  const [alert, setAlert] = useState(null);
  const timerRef = useRef(null);

  useTransparentBody();

  useEffect(() => {
    if (!socket || !room) return undefined;

    const joinRoom = () => {
      for (const targetRoom of rooms) {
        socket.emit("join", { room: targetRoom, role: "obs" });
        socket.emit("chat:join", { room: targetRoom, role: "obs" });
      }
    };

    const leaveRoom = () => {
      for (const targetRoom of rooms) {
        socket.emit("leave", { room: targetRoom, role: "obs" });
        socket.emit("chat:leave", { room: targetRoom, role: "obs" });
      }
    };

    const onConnect = () => joinRoom();

    const onSpecialArrival = (payload = {}) => {
      const payloadRoom = String(payload?.room || "").trim();
      if (!roomMatches(payloadRoom, rooms)) return;

      const next = {
        id: `${payload?.key || "special"}-${payload?.ts || Date.now()}`,
        title: String(payload?.overlayText || payload?.title || "Long-time supporter checked in").trim(),
        subtitle: String(payload?.subText || payload?.subtitle || "GA family in the building").trim(),
        tone: String(payload?.tone || "special").trim(),
      };

      setAlert(next);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      const ttl = Math.max(2500, Math.min(15000, Number(payload?.ttlMs || 8500)));
      timerRef.current = window.setTimeout(() => {
        setAlert((current) => (current?.id === next.id ? null : current));
        timerRef.current = null;
      }, ttl);
    };

    socket.on("connect", onConnect);
    socket.on("chat:special-arrival", onSpecialArrival);

    if (!socket.connected) {
      socket.connect();
    } else {
      joinRoom();
    }

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      leaveRoom();
      socket.off("connect", onConnect);
      socket.off("chat:special-arrival", onSpecialArrival);
    };
  }, [room, rooms]);

  return <SpecialArrivalCard alert={alert} />;
}
